import {useEffect, useRef, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {toast} from 'react-toastify';
import SignatureCanvas from '../../components/signatureCanvas';
import {ATTENDANCE_WINDOW_CLOSED, ATTENDANCE_CLOSED_MESSAGE} from '../../utils/digitalAttendance';
import {
  getPublicAttendanceContext,
  lookupPublicMeetingAttendee,
  signPublicMeetingAttendance
} from '../../API/apiService';
import './style.scss';

const EMPTY_SATISFACTION = {
  teacherSatisfaction: '', administrativeSatisfaction: '', recommendationScore: '',
  moduleRelevanceScore: '', sessionTakeaway: '', programComments: ''
};

function ScaleQuestion({number, children, maximum, value, onChange, help}) {
  return <fieldset className='public-meeting-attendance__question'>
    <legend><strong>{number}.</strong> {children}</legend>
    <p>{help}</p>
    <div className='public-meeting-attendance__scale'>
      {Array.from({length: maximum}, (_, index) => index + 1).map(option =>
        <label key={option}>
          <input type='radio' name={`satisfaction-${number}`} value={option}
                 checked={Number(value) === option} onChange={() => onChange(option)} />
          <span>{option}</span>
        </label>
      )}
    </div>
  </fieldset>;
}

export default function PublicMeetingAttendance() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const signatureRef = useRef(null);
  const [context, setContext] = useState(null);
  const [document, setDocument] = useState('');
  const [person, setPerson] = useState(null);
  const [nameConfirmed, setNameConfirmed] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [attendanceClosed, setAttendanceClosed] = useState(false);
  const [satisfaction, setSatisfaction] = useState(EMPTY_SATISFACTION);
  const isSatisfactionMode = context?.registrationMode === 'attendance_satisfaction';

  useEffect(() => {
    getPublicAttendanceContext(token).then(data => {
      if (data?.error === ATTENDANCE_WINDOW_CLOSED) setAttendanceClosed(true);
      if (['attendance_only', 'attendance_satisfaction'].includes(data?.registrationMode)) setContext(data);
      setLoading(false);
    });
  }, [token]);

  async function findPerson(event) {
    event.preventDefault();
    const normalized = document.trim();
    if (!normalized) return toast('Ingrese el número de documento', {type: 'warning'});
    setSubmitting(true);
    const result = await lookupPublicMeetingAttendee(token, normalized);
    setSubmitting(false);
    if (result?.error === ATTENDANCE_WINDOW_CLOSED) {
      setAttendanceClosed(true);
      return;
    }
    if (result?.found === false || result?.error === 'BENEFICIARY_NOT_FOUND') {
      return toast('Este documento no está caracterizado en este grupo', {type: 'warning'});
    }
    if (result?.error) return toast('No fue posible consultar el documento', {type: 'error'});
    if (result.alreadySigned) {
      return toast('Este documento ya tiene una firma asociada a este encuentro', {type: 'info'});
    }
    setPerson(result);
    setNameConfirmed(false);
  }

  function rejectPerson() {
    setPerson(null);
    setDocument('');
    setNameConfirmed(false);
    setHasSignature(false);
    setSatisfaction(EMPTY_SATISFACTION);
  }

  function updateSatisfaction(field, value) {
    setSatisfaction(prev => ({...prev, [field]: value}));
  }

  async function submitSignature() {
    if (!nameConfirmed) {
      return toast('Primero confirme que el nombre es correcto', {type: 'warning'});
    }
    if (!signatureRef.current?.hasDrawn()) {
      return toast('Firme en el recuadro antes de continuar', {type: 'warning'});
    }
    if (isSatisfactionMode && (
      !satisfaction.teacherSatisfaction || !satisfaction.administrativeSatisfaction ||
      !satisfaction.recommendationScore || !satisfaction.moduleRelevanceScore ||
      !satisfaction.sessionTakeaway.trim() || !satisfaction.programComments.trim()
    )) return toast('Responda todas las preguntas obligatorias', {type: 'warning'});
    setSubmitting(true);
    const result = await signPublicMeetingAttendance({
      token,
      beneficiaryId: person.beneficiaryId,
      document: person.document,
      nameConfirmed: true,
      signature: signatureRef.current.toDataURL(),
      ...(isSatisfactionMode ? {satisfaction} : {})
    });
    setSubmitting(false);
    if (result?.error === ATTENDANCE_WINDOW_CLOSED) {
      setAttendanceClosed(true);
      return;
    }
    if (['ATTENDANCE_ALREADY_SIGNED', 'SATISFACTION_ALREADY_SUBMITTED'].includes(result?.error)) {
      return toast(isSatisfactionMode
        ? 'Este documento ya respondió el formulario para este encuentro'
        : 'Este documento ya tiene una firma asociada a este encuentro', {type: 'info'});
    }
    if (result?.error) return toast('No fue posible guardar la firma', {type: 'error'});
    setCompleted(true);
  }

  if (loading) return <main className='public-meeting-attendance'><section>Cargando...</section></main>;
  if (attendanceClosed) return <main className='public-meeting-attendance'><section><p>{ATTENDANCE_CLOSED_MESSAGE}</p></section></main>;
  if (!context) return <main className='public-meeting-attendance'><section><h1>Enlace no válido</h1><p>El enlace expiró o no corresponde a un encuentro.</p></section></main>;
  if (completed) return <main className='public-meeting-attendance'><section><span className='material-symbols-rounded public-meeting-attendance__success'>check_circle</span><h1>{isSatisfactionMode ? 'Asistencia y formulario registrados' : 'Asistencia registrada'}</h1><p>Gracias, la información fue guardada correctamente.</p></section></main>;

  const meeting = context.meetings?.[0];
  return (
    <main className='public-meeting-attendance'>
      <section>
        <h1>{isSatisfactionMode ? 'Asistencia y formulario de satisfacción' : 'Firma de asistencia'}</h1>
        <p className='public-meeting-attendance__context'>
          {context.group?.code}{meeting?.order ? ` · Encuentro ${meeting.order}` : ''}
        </p>
        {!person ? (
          <form onSubmit={findPerson}>
            <label htmlFor='attendance-document'>Número de documento</label>
            <input id='attendance-document' inputMode='numeric' autoComplete='off'
                   value={document} onChange={event => setDocument(event.target.value)} />
            <button disabled={submitting}>{submitting ? 'Consultando...' : 'Continuar'}</button>
          </form>
        ) : (
          <div>
            <div className='public-meeting-attendance__confirmation'>
              <p>Este documento está asociado a:</p>
              <strong>{person.fullName}</strong>
              <p>¿Es correcto?</p>
              <div>
                <button type='button' className='secondary' onClick={rejectPerson}>No, corregir</button>
                {!nameConfirmed && (
                  <button type='button' onClick={() => setNameConfirmed(true)}>
                    Sí, es correcto
                  </button>
                )}
                {nameConfirmed && <span className='confirmed'>Nombre confirmado</span>}
              </div>
            </div>
            {nameConfirmed && (
              <>
                {isSatisfactionMode && <div className='public-meeting-attendance__survey'>
                  <ScaleQuestion number={1} maximum={5} value={satisfaction.teacherSatisfaction}
                    onChange={value => updateSatisfaction('teacherSatisfaction', value)}
                    help='Escala de 1 a 5 (1 = malo, 5 = excelente)'>
                    ¿Cómo califica el grado de satisfacción con el docente en cuanto al proceso de enseñanza y su dominio en los temas abordados durante el programa?
                  </ScaleQuestion>
                  <ScaleQuestion number={2} maximum={5} value={satisfaction.administrativeSatisfaction}
                    onChange={value => updateSatisfaction('administrativeSatisfaction', value)}
                    help='Escala de 1 a 5 (1 = malo, 5 = excelente)'>
                    ¿La información y atención brindada por el personal Administrativo de la EIA fue satisfactoria?
                  </ScaleQuestion>
                  <ScaleQuestion number={3} maximum={10} value={satisfaction.recommendationScore}
                    onChange={value => updateSatisfaction('recommendationScore', value)}
                    help='Escala de 1 a 10 (1 = nada probable, 10 = muy probable)'>
                    ¿Qué tan probable es que recomiendes esta sesión a otros?
                  </ScaleQuestion>
                  <ScaleQuestion number={4} maximum={10} value={satisfaction.moduleRelevanceScore}
                    onChange={value => updateSatisfaction('moduleRelevanceScore', value)}
                    help='Escala de 1 a 10 (1 = nada pertinente, 10 = muy pertinente)'>
                    ¿Qué tan pertinente fue este módulo para tu formación?
                  </ScaleQuestion>
                  <label htmlFor='session-takeaway'><strong>5.</strong> ¿Con qué palabra o idea te quedas de esta sesión?</label>
                  <textarea id='session-takeaway' maxLength={1000} value={satisfaction.sessionTakeaway}
                    onChange={event => updateSatisfaction('sessionTakeaway', event.target.value)} />
                  <label htmlFor='program-comments'><strong>6.</strong> ¿Tiene algún comentario o recomendación sobre el programa?</label>
                  <textarea id='program-comments' maxLength={2000} value={satisfaction.programComments}
                    onChange={event => updateSatisfaction('programComments', event.target.value)} />
                  <h2>7. Firma</h2>
                </div>}
                <SignatureCanvas ref={signatureRef} height={210} onDrawChange={setHasSignature} />
                <button type='button' className='submit-signature' disabled={!hasSignature || submitting}
                        onClick={submitSignature}>
                  {submitting ? 'Guardando...' : isSatisfactionMode
                    ? 'Confirmar asistencia y formulario'
                    : 'Confirmar firma y asistencia'}
                </button>
              </>
            )}
          </div>
        )}
      </section>
    </main>
  );
}
